import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Search,
  X,
  Paperclip,
  CheckCircle2,
  RefreshCw,
  Mail,
  Phone,
} from "lucide-react";

import TextInputComp from "../../components/customer/TextInputComp";
import Dropdown, { Option } from "../../components/customer/Dropdown";
import CommonButton from "../../components/CommonButton";

import {
  useUploadCustomerFileMutation,
  useLazyContactUsDetailsQuery,
} from "../../redux/actions/Customer/profileCustomer";

import { useLazyGetCustomerOrdersQuery } from "../../redux/actions/Customer/cartCustomer";
import { useCreateQueryMutation } from "../../redux/actions/Customer/FooterCustomer";

import { ICreateContactRequest } from "../../types/Request/CustomerRequest";
import { QuerySubject } from "../../types/Response/CustomerResponse";
import { ImagePath } from "../../assests";

interface ContactUsLocationState {
  orderId?: number;
  orderNumber?: string;
}

interface ContactusPageProps {
  orderId?: number;
  orderNumber?: string;
  onBack?: () => void;
}

interface IContactUsDetails {
  phoneNumber: string;
  whatsAppNumber: string;
  email: string;
}

const ContactusPage: React.FC<ContactusPageProps> = ({
  orderId: propOrderId,
  orderNumber: propOrderNumber,
}) => {
  const location = useLocation();
  const state = (location.state as ContactUsLocationState) || {};

  // Pre-linked order metadata from props or location state
  const initialOrderId = propOrderId ?? state.orderId;
  const initialOrderNumber = propOrderNumber ?? state.orderNumber;

  // Map enum options to the dropdown format
  const subjectOptions: Option[] = [
    {
      id: String(QuerySubject.GeneralInquiry),
      label: "General Inquiry",
      value: "GeneralInquiry",
    },
    {
      id: String(QuerySubject.OrderSupport),
      label: "Order Support",
      value: "OrderSupport",
    },
    {
      id: String(QuerySubject.GroomingServices),
      label: "Grooming Services",
      value: "GroomingServices",
    },
    {
      id: String(QuerySubject.VendorPartnership),
      label: "VendorPartnership",
      value: "VendorPartnership",
    },
    {
      id: String(QuerySubject.PetTaxi),
      label: "Pet Taxi",
      value: "PetTaxi",
    },
  ];

  const [selectedSort, setSelectedSort] = useState<Option | null>(
    initialOrderId || initialOrderNumber
      ? subjectOptions[1]
      : subjectOptions[0]
  );

  // Dynamic Selected Order state
  const [selectedOrderId, setSelectedOrderId] = useState<
    number | undefined
  >(initialOrderId);

  const [selectedOrderNumber, setSelectedOrderNumber] = useState<
    string | undefined
  >(initialOrderNumber);

  // Searchable Order Dropdown States
  const [orderSearchTerm, setOrderSearchTerm] = useState<string>(
    initialOrderNumber ? `Order #${initialOrderNumber}` : ""
  );

  const [isOrderDropdownOpen, setIsOrderDropdownOpen] =
    useState<boolean>(false);

  // Form Field States
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  // Attachment & UI States
  const [attachments, setAttachments] = useState<
    { file: File; url: string }[]
  >([]);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    [key: string]: string | null;
  }>({});

  // RTK Query API Hooks
  const [
    getCustomerOrders,
    { data: customerOrders, isLoading: isLoadingOrders },
  ] = useLazyGetCustomerOrdersQuery();

  const [uploadFile] = useUploadCustomerFileMutation();

  const [createQuery, { isLoading: isSubmitting }] =
    useCreateQueryMutation();

  // Contact Us Details API
  const [
    getContactUsDetails,
    {
      data: contactUsDetails,
      isLoading: isLoadingContactUs,
    },
  ] = useLazyContactUsDetailsQuery();

  const orders = customerOrders?.orders ?? [];

  // Fetch Contact Us details
  useEffect(() => {
    getContactUsDetails();
  }, [getContactUsDetails]);

  // Searchable Filtered Orders
  const filteredOrders = useMemo(() => {
    if (!orderSearchTerm.trim()) return orders;

    const term = orderSearchTerm
      .toLowerCase()
      .replace("order #", "");

    return orders.filter(
      (o) =>
        o.orderNumber?.toLowerCase().includes(term) ||
        o.orderId?.toString().includes(term)
    );
  }, [orders, orderSearchTerm]);

  // Fetch orders when "Order Support" is selected and order list is empty
  useEffect(() => {
    if (
      selectedSort?.id === String(QuerySubject.OrderSupport) &&
      orders.length === 0
    ) {
      getCustomerOrders({
        Search: "",
        Status: "",
        Page: 1,
        PageSize: 50,
      });
    }
  }, [selectedSort, orders.length, getCustomerOrders]);

  // Sync pre-linked order into message template
  useEffect(() => {
    const activeOrderNum =
      selectedOrderNumber || initialOrderNumber;

    const activeOrderId =
      selectedOrderId || initialOrderId;

    if (activeOrderNum || activeOrderId) {
      setSelectedSort(subjectOptions[1]);

      setMessage(
        `Hi Support Team,\n\nI need assistance regarding my order #${
          activeOrderNum || activeOrderId
        }.\n\nIssue Details:\n`
      );
    }
  }, [
    selectedOrderNumber,
    selectedOrderId,
    initialOrderNumber,
    initialOrderId,
  ]);

  // Subject Dropdown Change Handler
  const handleSubjectChange = (option: Option | null) => {
    setSelectedSort(option);

    if (
      option?.id !== String(QuerySubject.OrderSupport) &&
      !initialOrderNumber
    ) {
      setSelectedOrderId(undefined);
      setSelectedOrderNumber(undefined);
      setOrderSearchTerm("");
      setMessage("");
    }
  };

  // Order Search Dropdown Selection Handler
  const handleSelectOrder = (order: any) => {
    setSelectedOrderId(order.orderId);
    setSelectedOrderNumber(order.orderNumber);
    setOrderSearchTerm(`Order #${order.orderNumber}`);
    setIsOrderDropdownOpen(false);
  };

  // File Upload Stream Handler
  const handleFileUploadStream = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const selectedFiles = Array.from(e.target.files);

    setIsUploading(true);

    try {
      for (const file of selectedFiles) {
        const response = await uploadFile({
          File: file,
          FolderName: "Support/Attachments",
        }).unwrap();

        setAttachments((prev) => [
          ...prev,
          {
            file,
            url: response.fileUrl,
          },
        ]);
      }
    } catch (error) {
      console.error(
        "Failed to upload attachment file stream:",
        error
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Remove individual attachment
  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  // Form Validation
  const validateForm = () => {
    const newErrors: {
      [key: string]: string | null;
    } = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    }

    if (!message.trim()) {
      newErrors.message =
        "Please describe your issue";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Submit Support Query Handler
  const handleSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload: ICreateContactRequest = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      subject:
        selectedSort?.value || "GeneralInquiry",
      orderId: selectedOrderId
        ? Number(selectedOrderId)
        : null,
      orderNumber:
        selectedOrderNumber ||
        initialOrderNumber ||
        null,
      message: message.trim(),
      attachments: attachments.map(
        (att) => att.url
      ),
    };

    try {
      await createQuery(payload).unwrap();
      setSubmitSuccess(true);
    } catch (err) {
      console.error(
        "Failed to submit contact query:",
        err
      );
    }
  };

  const activeDisplayOrderNumber =
    selectedOrderNumber || initialOrderNumber;

  return (
    <div className="max-w-7xl space-y-5 mx-auto">
      {/* Header Section */}
      <section
        className="relative w-full overflow-hidden rounded-2xl bg-cover bg-center bg-no-repeat flex items-center justify-center min-h-[280px] md:min-h-[360px] px-4 py-12"
        style={{
         
          backgroundImage: `url(${ImagePath.petLandBackground})`,
          backgroundPosition: "center 40%",
        }}
      >
        <div className="absolute inset-0 bg-black/40 z-0"></div>

        <div className="max-w-3xl mx-auto text-center relative z-10 space-y-3">
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
            {activeDisplayOrderNumber
              ? `Support for Order #${activeDisplayOrderNumber}`
              : "We'd Love to Hear From You!"}
          </h1>

          <p className="text-xs sm:text-sm md:text-base text-white/90 max-w-2xl mx-auto font-medium leading-relaxed">
            {activeDisplayOrderNumber
              ? "Need help with this order? Attach photos of the issue if relevant and send us a message below."
              : "Have questions about pet supplies, grooming services, or your order? Our team is here to help."}
          </p>
        </div>
      </section>

      {/* Main Form Grid */}
      <main className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start px-4 md:px-8 py-6">
        <div className="md:col-span-2 bg-white rounded-2xl shadow-xs p-6 md:p-8 border border-slate-100 space-y-4">
          <h2 className="text-2xl font-bold text-[#2F5E2E]">
            Send Us a Message
          </h2>

          {/* Context Banner if Order-Linked */}
          {activeDisplayOrderNumber && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3.5 rounded-xl text-xs font-semibold flex items-center justify-between">
              <span>
                Linked Order:{" "}
                <strong>
                  #{activeDisplayOrderNumber}
                </strong>
              </span>

              <span className="bg-emerald-200/60 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                Order Support Active
              </span>
            </div>
          )}

          {submitSuccess ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center space-y-3">
              <CheckCircle2
                size={48}
                className="text-[#6DBE45] mx-auto"
              />

              <h3 className="text-xl font-bold text-[#2F5E2E]">
                Support Request Received!
              </h3>

              <p className="text-xs text-slate-600 max-w-md mx-auto">
                Thank you for reaching out. Our support
                team is reviewing your details and will
                get back to you shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <TextInputComp
                  label="Your Name"
                  placeholder="John Doe"
                  value={name}
                  onChangeText={(text) =>
                    setName(text)
                  }
                  showLabel={true}
                  showStar={true}
                  error={errors.name}
                />

                <TextInputComp
                  type="email"
                  label="Email Address"
                  placeholder="john@example.com"
                  value={email}
                  onChangeText={(text) =>
                    setEmail(text)
                  }
                  showLabel={true}
                  showStar={true}
                  error={errors.email}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 items-center">
                <TextInputComp
                  type="tel"
                  label="Phone Number"
                  placeholder="+971 50 123 4567"
                  value={phone}
                  onChangeText={(text) =>
                    setPhone(text)
                  }
                  showLabel={true}
                  error={errors.phone}
                />

                <div className="py-2">
                  <Dropdown
                    options={subjectOptions}
                    value={selectedSort}
                    onChange={handleSubjectChange}
                    className="w-full"
                    iconPosition="none"
                    label="Subject"
                    showLabel={true}
                  />
                </div>
              </div>

              {/* Searchable Order Selector */}
              {selectedSort?.id ===
                String(QuerySubject.OrderSupport) &&
                !initialOrderNumber && (
                  <div className="py-1 relative">
                    <label className="pb-1.5 block text-xs font-bold text-[#2F5E2E] tracking-wider">
                      Select Order Having Issue
                    </label>

                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                      />

                      <input
                        type="text"
                        placeholder={
                          isLoadingOrders
                            ? "Loading your orders..."
                            : "Type order number or search..."
                        }
                        value={orderSearchTerm}
                        onFocus={() =>
                          setIsOrderDropdownOpen(true)
                        }
                        onChange={(e) => {
                          setOrderSearchTerm(
                            e.target.value
                          );
                          setIsOrderDropdownOpen(true);
                        }}
                        className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-800 bg-white focus:outline-none focus:border-[#6DBE45] focus:ring-1 focus:ring-[#6DBE45]"
                      />

                      {selectedOrderNumber && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrderId(
                              undefined
                            );
                            setSelectedOrderNumber(
                              undefined
                            );
                            setOrderSearchTerm("");
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Search Dropdown Results Menu */}
                    {isOrderDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto z-30 divide-y divide-slate-100">
                        {filteredOrders.length > 0 ? (
                          filteredOrders.map(
                            (order) => (
                              <div
                                key={order.orderId}
                                onClick={() =>
                                  handleSelectOrder(
                                    order
                                  )
                                }
                                className="p-3 hover:bg-emerald-50/60 cursor-pointer flex items-center justify-between text-xs transition-colors"
                              >
                                <div>
                                  <p className="font-bold text-slate-800">
                                    Order #
                                    {
                                      order.orderNumber
                                    }
                                  </p>

                                  <p className="text-[10px] text-slate-400 mt-0.5">
                                    {new Date(
                                      order.orderDate
                                    ).toLocaleDateString()}{" "}
                                    •{" "}
                                    {
                                      order.primaryItemName
                                    }
                                  </p>
                                </div>

                                <span className="font-mono font-bold text-[#2F5E2E]">
                                  AED{" "}
                                  {order.totalAmount.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                            )
                          )
                        ) : (
                          <div className="p-4 text-center text-xs text-slate-400 italic">
                            {isLoadingOrders
                              ? "Loading orders..."
                              : "No matching orders found."}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              {/* Message Description */}
              <div className="py-1">
                <label className="pb-2 block text-sm text-[#2F5E2E] font-bold">
                  Issue Description / Request
                </label>

                <textarea
                  rows={5}
                  required
                  value={message}
                  onChange={(e) =>
                    setMessage(e.target.value)
                  }
                  className="w-full p-3.5 rounded-xl text-sm text-slate-800 border border-slate-300 focus:outline-none focus:border-[#6DBE45] focus:ring-1 focus:ring-[#6DBE45] placeholder:text-slate-400 bg-white"
                  placeholder="Describe your issue or request here..."
                ></textarea>

                {errors.message && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.message}
                  </p>
                )}
              </div>

              {/* File Upload Attachment */}
              <div className="py-1 space-y-2">
                <label className="block text-xs font-bold uppercase text-slate-500 tracking-wider">
                  Attach Images or Proof (Optional)
                </label>

                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100 transition-colors border border-emerald-200">
                    <Paperclip size={14} />

                    <span>Upload Proof Files</span>

                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={
                        handleFileUploadStream
                      }
                      className="hidden"
                    />
                  </label>

                  {isUploading && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                      <RefreshCw
                        size={13}
                        className="animate-spin text-[#6DBE45]"
                      />
                      Uploading files...
                    </span>
                  )}
                </div>

                {/* File Previews */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {attachments.map(
                      (item, idx) => (
                        <div
                          key={idx}
                          className="relative group w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-slate-50"
                        >
                          <img
                            src={
                              item.url ||
                              URL.createObjectURL(
                                item.file
                              )
                            }
                            alt="preview"
                            className="w-full h-full object-cover"
                          />

                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveAttachment(
                                idx
                              )
                            }
                            className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>

              <CommonButton
                text={
                  isSubmitting
                    ? "Submitting Request..."
                    : "Submit Support Request"
                }
                variant="filled"
                rounded="md"
                disabled={
                  isSubmitting || isUploading
                }
                className="w-full sm:w-auto px-8"
              />
            </form>
          )}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-xs p-6 border border-slate-100">
            <h3 className="text-xl font-bold mb-4 text-[#2F5E2E]">
              Get in Touch
            </h3>

            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center gap-4">
                <div className="bg-[#A8D97B]/20 text-[#8B6B3E] p-3 rounded-xl shrink-0">
                  <Mail
                    size={20}
                    strokeWidth={2}
                  />
                </div>

                <div>
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
                    Email Us
                  </p>

                  {isLoadingContactUs ? (
                    <p className="text-sm text-slate-400">
                      Loading...
                    </p>
                  ) : (
                    <a
                      href={
                        contactUsDetails?.email
                          ? `mailto:${contactUsDetails.email}`
                          : "#"
                      }
                      className="text-slate-700 font-medium hover:text-[#5FB63F] transition block"
                    >
                      {contactUsDetails?.email ||
                        "N/A"}
                    </a>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4">
                <div className="bg-[#A8D97B]/20 text-[#8B6B3E] p-3 rounded-xl shrink-0">
                  <Phone
                    size={20}
                    strokeWidth={2}
                  />
                </div>

                <div>
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
                    Contact Us
                  </p>

                  {isLoadingContactUs ? (
                    <p className="text-sm text-slate-400">
                      Loading...
                    </p>
                  ) : (
                    <a
                      href={
                        contactUsDetails?.phoneNumber
                          ? `tel:${contactUsDetails.phoneNumber}`
                          : "#"
                      }
                      className="text-slate-700 font-medium hover:text-[#5FB63F] transition block"
                    >
                      {contactUsDetails?.phoneNumber ||
                        "N/A"}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactusPage;